import { useMemo } from "react";
import { useQuery } from "react-query";

import { selectSelectedWells, selectTxnId, useMapStore } from "stores/map";
import { expandSelectedWellsToUwis } from "utils/groupModeHelpers";

import { ENTITIES_ON_MAP_STALE_TIME, fetchRawMapEntities } from "api/map";

/**
 * Simple `hook` that abstracts API call to fetch mapEntities.
 *
 * Deduplication strategy:
 *   - `enabled` gates the query on txnId + wells being present. React Query
 *     automatically fires when the queryKey changes (new txnId or new well
 *     selection) and coalesces concurrent calls from multiple mounted consumers
 *     (XdaViewer, ThreeDViewer, DashboardPane) into a single in-flight request.
 *   - `staleTime: ENTITIES_ON_MAP_STALE_TIME` (Infinity) prevents background
 *     refetches for a given key, since txnId is immutable for its lifetime.
 */
const useMapEntitiesQuery = () => {
  const txnId = useMapStore(selectTxnId);
  const selectedWells = useMapStore(selectSelectedWells);

  // In group mode, selectedWells keys are group composite IDs, not real UWIs.
  // Expand to constituent UWIs so fetchRawMapEntities can resolve MapEntity records.
  // In single mode, well.Uwi is used (canonical field) rather than the map key.
  const uwiList = useMemo(
    () => expandSelectedWellsToUwis(selectedWells),
    [selectedWells]
  );

  // txnId/uwiList in the queryKey ensure a fresh fetch on any change.
  // React Query deduplicates concurrent calls sharing the same key — the second
  // subscriber receives the cached in-flight promise rather than firing a new request.
  const queryKey = ["mapEntities", txnId?.id, ...uwiList];
  const queryFn = () => fetchRawMapEntities(txnId?.id, uwiList);

  const { data, refetch, isError, isFetching } = useQuery(queryKey, queryFn, {
    enabled: !!(txnId?.id && uwiList.length > 0),
    staleTime: ENTITIES_ON_MAP_STALE_TIME
  });

  return {
    mapEntities: data,
    refetchMapEntities: refetch,
    mapEntitiesError: isError,
    mapEntitiesFetching: isFetching
  };
};

export default useMapEntitiesQuery;
